import React, { useEffect, useRef, type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { ButtonGroup } from '@atlaskit/button';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { Intl, FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import { NEW_MODE } from '../../common/constants';
import { ExplanationContainer } from '../../common/ui/styled';
import { useGoalCounter } from '../../controllers/goal-counter';
import { useMetricActions } from '../../controllers/metric-actions';
import { AwarenessBannerForHierarchicalGoals } from './awareness-banner';
import { Goal } from './goal-counter';
import messages from './messages';

const LEARN_MORE_ABOUT_IMPROVED_SLAS =
	'https://support.atlassian.com/jira-service-management-cloud/docs/use-priority-to-group-slas-early-access-feature/';

type ActionProps = {
	formatMessage: FormatMessage;
	onNewMetrickClick: (e: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
	onToggleCalendarPopup: () => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ref: any;
};

const headerActions = ({
	onNewMetrickClick,
	formatMessage,
	onToggleCalendarPopup,
	ref,
}: ActionProps) => (
	<ButtonGroup>
		<Button
			ref={ref}
			onClick={onNewMetrickClick}
			testId="servicedesk-sla-settings.ui.header.add-new-sla"
		>
			{formatMessage(messages.addNewSla)}
		</Button>
		<Button
			onClick={onToggleCalendarPopup}
			testId="servicedesk-sla-settings.ui.header.calendar"
			iconAfter={<CalendarIcon label={formatMessage(messages.calendarSelection)} />}
		/>
	</ButtonGroup>
);

type Props = Intl & {
	showActions: boolean;
	onToggleCalendarPopup: () => void;
	showBanner: boolean;
};

export const HeaderComponent = ({
	intl: { formatMessage },
	onToggleCalendarPopup,
	showActions,
	showBanner,
}: Props) => {
	const [, { updatePath }] = useMetricActions();

	const handleNewMetricClick = (
		e: SyntheticEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		fireUIAnalytics(analyticsEvent, 'newSla');
		updatePath(null, NEW_MODE);
	};

	const onClickLearnAboutImprovedSLAs = () => {
		window.open(LEARN_MORE_ABOUT_IMPROVED_SLAS, '_blank');
	};
	const addSlaButtonRef = useRef<HTMLElement>(null);

	const [{ shouldFocusOnAddSlaButton: isFocus }, actions] = useGoalCounter();

	useEffect(() => {
		if (isFocus) {
			addSlaButtonRef?.current?.focus();
			actions.setShouldFocusOnAddSlaButton(false);
		}
	}, [actions, isFocus]);

	return (
		<>
			<PageHeader
				actions={
					showActions &&
					headerActions({
						ref: addSlaButtonRef,
						onNewMetrickClick: handleNewMetricClick,
						formatMessage,
						onToggleCalendarPopup,
					})
				}
			>
				{formatMessage(messages.slaTitle)}
			</PageHeader>
			<ExplanationContainer>{formatMessage(messages.slaDescriptionJsm)}</ExplanationContainer>
			{showBanner && (
				<AwarenessBannerForHierarchicalGoals
					formatMessage={formatMessage}
					onClickLearnAboutImprovedSLAs={onClickLearnAboutImprovedSLAs}
				/>
			)}
			{showActions && <Goal />}
		</>
	);
};

export const Header = injectIntl(HeaderComponent);
